import React, { useState } from "react";
import { sideNavData } from "../../data/side_nav_data";
import { NavLink, useLocation } from "react-router-dom";
import { Logo, Logout, Menu } from "../../assets/svgIcons";
import ProfileMenu from "../../components/profile-menu";

const Aside = ({ showAside, setShowAside }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const location = useLocation();

  // Filter nav items based on search query
  const filteredNavData = sideNavData.filter((item) => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    if (item?.label?.toLowerCase().includes(lowerQuery)) return true;
    return item?.subLinks?.some((sub) => sub?.label?.toLowerCase().includes(lowerQuery));
  });

  // Check if any sublink is active
  const isParentActive = (subLinks) => {
    if (!subLinks) return false;
    return subLinks.some(sub => sub?.path === location.pathname);
  };

  // Toggle expanded state for dropdown items
  const toggleExpanded = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <aside className={`sticky top-0 max-h-screen ${showAside ? '!min-w-[300px]' : '!max-w-[0px]'} bg-white shadow-lg border-r border-gray-200 transform overflow-auto transition-all duration-300 ease-in-out z-40 ${showAside ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
      {/* Header with Logo */}
      <div className="p-6 border-b sticky top-0 z-50 border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <button
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
            onClick={() => setShowAside(!showAside)}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              {Menu}
            </div>
          </button>
          <NavLink to="/" className="flex items-center space-x-3 group">
            <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-lg">
              {Logo}
            </div>
            <span className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
              Dashboard
            </span>
          </NavLink>
        </div>
      </div>

      {/* Search Input */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <input
            type="text"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200"
          />
          <div className="absolute left-1.5 top-1/2 transform -translate-y-1/2">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto py-4">
        {filteredNavData && filteredNavData?.length ? (
          <ul className="space-y-1 px-3">
            {filteredNavData.map((item, index) => {
              if (item?.label) {
                const hasSubLinks = item?.subLinks && item?.subLinks?.length;
                const isParentCurrentlyActive = isParentActive(item?.subLinks);

                // Show sublinks that match search if searching
                const visibleSubLinks = hasSubLinks
                  ? item.subLinks.filter((sub) =>
                    !searchQuery || sub?.label?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  : [];

                if (hasSubLinks && visibleSubLinks.length === 0 && searchQuery) return null;

                return (
                  <li key={index} className="relative">
                    {!hasSubLinks ? (
                      <NavLink
                        to={item?.path}
                        className={({ isActive }) =>
                          `flex items-center space-x-3 px-3 py-2.5 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 group ${
                            isActive
                              ? "bg-blue-50 text-blue-700 border-r-3 border-blue-600"
                              : ""
                          }`
                        }
                      >
                        <div className="w-5 h-5 flex items-center justify-center text-gray-500 group-hover:text-gray-700">
                          {item?.icon}
                        </div>
                        <span className="font-medium text-sm">{item?.label}</span>
                      </NavLink>
                    ) : (
                      <div className="space-y-1">
                        {/* Main menu item - clickable */}
                        <button
                          onClick={() => toggleExpanded(index)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 group ${
                            isParentCurrentlyActive ? "bg-blue-50 text-blue-700" : ""
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-5 h-5 flex items-center justify-center text-gray-500 group-hover:text-gray-700">
                              {item?.icon}
                            </div>
                            <span className="font-medium text-sm">{item?.label}</span>
                          </div>
                          <div className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${
                            expandedItems[index] ? "rotate-180" : ""
                          }`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {/* Submenu - show if expanded or if parent is active */}
                        {(expandedItems[index] || isParentCurrentlyActive) && (
                          <div className="ml-6 space-y-1">
                            <ul className="space-y-1">
                              {visibleSubLinks.map((sub_item, subIndex) => {
                                if (sub_item?.label) {
                                  return (
                                    <li key={subIndex}>
                                      <NavLink
                                        to={sub_item?.path}
                                        className={({ isActive }) =>
                                          `flex items-center space-x-3 px-3 py-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200 group ${
                                            isActive ? "bg-blue-100 text-blue-700 font-medium" : ""
                                          }`
                                        }
                                      >
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 group-hover:bg-gray-600"></div>
                                        <span className="text-sm">{sub_item?.label}</span>
                                      </NavLink>
                                    </li>
                                  );
                                }
                                return null;
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              }
              return null;
            })}
          </ul>
        ) : (
          <div className="text-center py-8 px-4">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">No menu items found</p>
          </div>
        )}
      </nav>

      {/* Logout Button */}
      <div className="p-4 pt-1 pb-1 border-t sticky bottom-0 z-50 border-gray-200 bg-gray-50">
        <button
          onClick={() => {
            localStorage.removeItem("account");
            window.location.href = "/";
          }}
          className="w-full flex items-center justify-center space-x-3 px-4 py-2.5 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-300 transition-all duration-200 group"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            {Logout}
          </div>
          <span className="font-medium text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Aside;